import { AlertCircle, RotateCcw, WandSparkles } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Badge } from '@/components/admin/shared/badge'
import type { ProcessingType } from './_lib/processing-trigger-policy'
import { ReprocessConfirmModal } from './reprocess-confirm-modal'

const PROCESSING_LABEL: Record<ProcessingType, string> = {
  none: 'None',
  process: 'Process',
  reprocess: 'Reprocess',
  full_reprocess: 'Full reprocess',
}

const PROCESSING_BADGE: Record<ProcessingType, string> = {
  none: 'default',
  process: 'active',
  reprocess: 'Medium',
  full_reprocess: 'Hard',
}

interface ReprocessControlBarProps {
  pendingCount: number
  dirtyTabs: string[]
  summaryLabel: string
  processingTypes: ProcessingType[]
  onOpenPendingChanges: () => void
  onRunProcessing: () => void
  canRevertToSnapshot: boolean
  onRevertToSnapshot: () => void
}

export function ReprocessControlBar({
  pendingCount,
  dirtyTabs,
  summaryLabel,
  processingTypes,
  onOpenPendingChanges,
  onRunProcessing,
  canRevertToSnapshot,
  onRevertToSnapshot,
}: ReprocessControlBarProps) {
  const [confirming, setConfirming] = useState(false)
  const isDirty = pendingCount > 0

  const chips = useMemo(() => {
    if (!isDirty) return []
    return processingTypes.map((type) => ({
      type,
      label: PROCESSING_LABEL[type],
      badge: PROCESSING_BADGE[type],
    }))
  }, [isDirty, processingTypes])

  return (
    <>
      <div
        className={`rounded-lg border p-3 ${
          isDirty
            ? 'border-amber-300 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10'
            : 'border-border bg-muted/30'
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-foreground text-sm font-semibold">
                Processing control
              </p>
              {isDirty ? (
                <button type="button" onClick={onOpenPendingChanges}>
                  <Badge variant="Medium">
                    <span className="inline-flex items-center gap-1">
                      <AlertCircle size={12} />
                      {pendingCount} pending
                    </span>
                  </Badge>
                </button>
              ) : (
                <Badge variant="active">Up to date</Badge>
              )}
              {chips.map((chip) => (
                <Badge key={chip.type} variant={chip.badge}>
                  {chip.label}
                </Badge>
              ))}
            </div>
            <p className="text-muted-foreground text-xs">{summaryLabel}</p>
            {dirtyTabs.length > 0 && (
              <p className="text-muted-foreground/90 text-[11px]">
                Dirty tabs: {dirtyTabs.join(', ')}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!isDirty}
              onClick={() => setConfirming(true)}
              className="bg-primary text-primary-foreground inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <WandSparkles size={14} />
              Run Processing
            </button>
            <button
              type="button"
              disabled={!canRevertToSnapshot}
              onClick={onRevertToSnapshot}
              className="text-muted-foreground hover:text-foreground hover:bg-muted inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw size={14} />
              Revert to Last Snapshot
            </button>
          </div>
        </div>
      </div>
      <ReprocessConfirmModal
        isOpen={confirming}
        pendingCount={pendingCount}
        summaryLabel={summaryLabel}
        processingTypes={processingTypes}
        onClose={() => setConfirming(false)}
        onProceed={() => {
          onRunProcessing()
          setConfirming(false)
        }}
      />
    </>
  )
}
